import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import {
  ChartBox, CHART_MARGIN, GRID, AXIS, xAxisProps, yAxisProps, colorAt,
  KitTooltip, renderRefLines, normaliseSeries, compactAxis, num, EmptyState,
} from "./_kit";

/**
 * Generic bar chart — grouped (default), stacked, or horizontal.
 * A single-series chart colours each category distinctly. A series may set
 * getColor(row) to colour its bar per data point (e.g. green/red by threshold).
 * @param {object[]} props.data - one row per category, keyed by xKey + series keys
 * @param {string} [props.xKey="x"]
 * @param {{key:string,name?:string,color?:string,stackId?:string,maxBarSize?:number,getColor?:(row:object)=>string}[]} props.series
 * @param {boolean} [props.stacked]
 * @param {boolean} [props.horizontal]
 * @param {boolean} [props.angledLabels] - angle long category labels to avoid overlap
 * @param {{x?:number,y?:number,label?:string}[]} [props.refLines]
 * @param {string} [props.unit]
 */
export function RBarChart({
  data = [], xKey = "x", series = [], refLines = [], stacked = false, horizontal = false, angledLabels = false,
  unit, height = 320, formatValue = num, formatAxis = compactAxis, radius = 4,
  animate = false, animationDuration = 700,
}) {
  if (!data.length || !series.length) return <EmptyState />;
  const items = normaliseSeries(series);
  const distributed = items.length === 1;

  const catAxisProps = angledLabels
    ? { ...xAxisProps, angle: -20, textAnchor: "end", interval: 0, height: 66, tick: { fontSize: 9, fill: AXIS } }
    : xAxisProps;

  const xAxis = horizontal
    ? <XAxis type="number" {...xAxisProps} tickFormatter={formatAxis} />
    : <XAxis type="category" dataKey={xKey} {...catAxisProps} />;
  const yAxis = horizontal
    ? <YAxis type="category" dataKey={xKey} {...yAxisProps} />
    : <YAxis type="number" {...yAxisProps} tickFormatter={formatAxis} />;

  return (
    <ChartBox $height={height}>
      <ResponsiveContainer>
        <BarChart data={data} layout={horizontal ? "vertical" : "horizontal"} margin={CHART_MARGIN}>
          <CartesianGrid stroke={GRID} vertical={horizontal} horizontal={!horizontal} />
          {xAxis}
          {yAxis}
          <Tooltip cursor={{ fill: "rgba(148,163,184,0.08)" }} content={<KitTooltip unit={unit} formatValue={formatValue} />} />
          {renderRefLines(refLines)}
          {items.map((s) => {
            const cells = s.getColor
              ? data.map((row, i) => <Cell key={i} fill={s.getColor(row)} />)
              : distributed
                ? data.map((_, i) => <Cell key={i} fill={colorAt(i)} />)
                : null;
            return (
              <Bar
                key={s.key}
                dataKey={s.key}
                name={s.name}
                stackId={s.stackId ?? (stacked ? "stack" : undefined)}
                fill={s.color}
                radius={stacked ? 0 : radius}
                maxBarSize={s.maxBarSize}
                isAnimationActive={animate}
                animationDuration={animationDuration}
              >
                {cells}
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
    </ChartBox>
  );
}
